import React, { useState, useEffect } from "react";
import { Table, Typography, Space, Input, message, Button, Form, Select, Card, Row, Col, Tag, Checkbox, Alert } from "antd";
import { SearchOutlined, ClearOutlined, DownloadOutlined } from '@ant-design/icons';
import AxiosOICStat from "AxiosOICStat";
import { useSelector } from 'react-redux';
import DatePicker from 'react-multi-date-picker';
import DatePanel from "react-multi-date-picker/plugins/date_panel"
import PresetValuesPlugin from '../datePickerPlugins/PresetValuesPlugin';

const { Text } = Typography;
const { Option } = Select;

export default function ImfConvertFetchTab({ onConvertSuccess }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 100, total: 0, totalPages: 0 });
  const [form] = Form.useForm();
  const [countryMappings, setCountryMappings] = useState([]);
  const [indicatorMappings, setIndicatorMappings] = useState([]);
  const [unavailableIndicators, setUnavailableIndicators] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const countriesState = useSelector((state) => state.countries);
  const [indicatorSearchFilter, setIndicatorSearchFilter] = useState('');
  const [selectedYears, setSelectedYears] = useState([]);

  useEffect(() => {
    fetchCountryMappings();
    fetchIndicatorMappings();
  }, []);

  const fetchCountryMappings = async () => {
    try {
      const res = await AxiosOICStat.get("/external_api/imf/country-mappings");
      setCountryMappings(res.data?.mappings || res.data || []);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Failed to load IMF country mappings.";
      message.error(detail);
    }
  };

  const fetchIndicatorMappings = async () => {
    try {
      const res = await AxiosOICStat.get("/external_api/imf/indicator-mappings");
      const mappings = res.data?.mappings || res.data || [];
      setIndicatorMappings(mappings);
      const unavailable = mappings.filter(m => m.status === false);
      setUnavailableIndicators(unavailable);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Failed to load IMF indicator mappings.";
      message.error(detail);
    }
  };

  const exportToCsv = async (rows) => {
    setExportLoading(true);
    try {
      if (!rows || !rows.length) {
        message.warning('No data to export');
        return;
      }

      let exportRows = rows;

      if (pagination && pagination.total && rows.length < pagination.total) {
        try {
          const values = form.getFieldsValue();
          const requestData = {
            ind_codes: values.ind_codes || undefined,
            c_code: values.c_code ?? undefined,
            page: 1,
            page_size: pagination.total || 1000000
          };
          const res = await AxiosOICStat.post("/external_api/imf/convert/bulk", requestData);
          exportRows = res.data?.data || [];
        } catch (err) {
          const detail = err?.response?.data?.detail || err?.message || 'Failed to fetch all data for export';
          message.error(detail);
          return;
        }
      }

      const indField = 'oic_ind_code';
      const countryField = 'c_code';
      const valueField = 'imf_value';

      const yearsSet = new Set();
      exportRows.forEach(r => { if (r && r.year != null) yearsSet.add(Number(r.year)); });
      const years = Array.from(yearsSet).sort((a, b) => a - b).map(y => String(y));

      const map = new Map();
      exportRows.forEach(r => {
        const ind = r[indField] != null ? String(r[indField]) : '';
        const c = r[countryField] != null ? String(r[countryField]) : '';
        const y = r.year != null ? String(r.year) : '';
        const v = r[valueField] != null ? r[valueField] : '';
        const key = ind + '||' + c;
        if (!map.has(key)) map.set(key, { ind, c, values: {} });
        if (y) map.get(key).values[y] = v;
      });

      const headers = ['ind_code', 'c_code', ...years];
      const csvRows = [];
      csvRows.push(headers.join(','));

      const entries = Array.from(map.values()).sort((a, b) => {
        if (a.ind === b.ind) return (a.c || '').localeCompare(b.c || '');
        return (a.ind || '').localeCompare(b.ind || '');
      });

      const quote = (v) => {
        if (v === null || v === undefined) return '';
        if (typeof v === 'string') return '"' + v.replace(/"/g, '""') + '"';
        return String(v);
      };

      entries.forEach(row => {
        const vals = [quote(row.ind), quote(row.c)];
        years.forEach(y => {
          const cell = row.values && Object.prototype.hasOwnProperty.call(row.values, y) ? row.values[y] : '';
          vals.push(quote(cell));
        });
        csvRows.push(vals.join(','));
      });

      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `imf_export_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } finally {
      setExportLoading(false);
    }
  };

  const handleSearch = async (values) => {
    if (!values.ind_codes || !values.ind_codes.length) {
      message.warning("Please select one or more OIC indicators");
      return;
    }

    let startYear = undefined;
    let endYear = undefined;
    if (selectedYears && selectedYears.length > 0) {
      const yearVals = [];
      selectedYears.forEach(range => {
        if (range.length === 2) {
          for (let i = range[0].year; i <= range[1].year; i++) {
            yearVals.push(i);
          }
        } else {
          yearVals.push(range[0].year);
        }
      });
      if (yearVals.length > 0) {
        startYear = Math.min(...yearVals);
        endYear = Math.max(...yearVals);
      }
    }

    setLoading(true);
    try {
      const pageSize = pagination.pageSize || 100;
      const requestData = {
        ind_codes: values.ind_codes || undefined,
        c_code: values.c_code ?? undefined,
        start_year: startYear,
        end_year: endYear,
        page: 1,
        page_size: pageSize
      };

      const res = await AxiosOICStat.post("/external_api/imf/convert/bulk", requestData);
      setData(res.data.data || []);
      setPagination({
        current: res.data.page,
        pageSize: res.data.page_size,
        total: res.data.total,
        totalPages: res.data.total_pages
      });
      message.success(`Found ${res.data.total} records`);
      if (onConvertSuccess) onConvertSuccess();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Failed to convert/fetch IMF data.";
      message.error(detail);
      setData([]);
      setPagination({ current: 1, pageSize: 100, total: 0, totalPages: 0 });
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = async (newPagination) => {
    const values = form.getFieldsValue();
    if (!values.ind_codes || !values.ind_codes.length) return;

    let startYear = undefined;
    let endYear = undefined;
    if (selectedYears && selectedYears.length > 0) {
      const yearVals = [];
      selectedYears.forEach(range => {
        if (range.length === 2) {
          for (let i = range[0].year; i <= range[1].year; i++) {
            yearVals.push(i);
          }
        } else {
          yearVals.push(range[0].year);
        }
      });
      if (yearVals.length > 0) {
        startYear = Math.min(...yearVals);
        endYear = Math.max(...yearVals);
      }
    }

    setLoading(true);
    try {
      const requestData = {
        ind_codes: values.ind_codes || undefined,
        c_code: values.c_code ?? undefined,
        start_year: startYear,
        end_year: endYear,
        page: newPagination.current,
        page_size: newPagination.pageSize
      };

      const res = await AxiosOICStat.post("/external_api/imf/convert/bulk", requestData);

      setData(res.data.data || []);
      setPagination({
        current: res.data.page,
        pageSize: res.data.page_size,
        total: res.data.total,
        totalPages: res.data.total_pages
      });
      if (onConvertSuccess) onConvertSuccess();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Failed to fetch page. Please try again.";
      message.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { title: "Country", dataIndex: "country_name", key: "country_name", render: (val) => val ? <Text>{val}</Text> : <Text type="secondary">—</Text>, sorter: (a, b) => (String(a.country_name || '').localeCompare(String(b.country_name || ''))) },
    { title: "Indicator Code", dataIndex: "oic_ind_code", key: "oic_ind_code", render: (val) => <Text strong>{val}</Text>, sorter: (a, b) => (Number(a.oic_ind_code) || 0) - (Number(b.oic_ind_code) || 0) },
    { title: "IMF Code", dataIndex: "imf_indicator_code", key: "imf_indicator_code", render: (val) => val ? <Text code>{val}</Text> : <Text type="secondary">—</Text>, sorter: (a, b) => (a.imf_indicator_code || '').localeCompare(b.imf_indicator_code || '') },
    { title: "Dataset", dataIndex: "dataset", key: "dataset", render: (val) => val ? <Tag color="blue">{val}</Tag> : <Text type="secondary">—</Text> },
    { title: "Year", dataIndex: "year", key: "year", render: (val) => val ? <Text>{val}</Text> : <Text type="secondary">—</Text>, sorter: (a, b) => (Number(a.year) || 0) - (Number(b.year) || 0) },
    { title: "IMF Value", dataIndex: "imf_value", key: "imf_value", render: (val) => val !== null && val !== undefined ? <Text>{typeof val === 'number' ? val.toFixed(4) : val}</Text> : <Text type="secondary">—</Text>, sorter: (a, b) => (Number(a.imf_value) || 0) - (Number(b.imf_value) || 0) },
    { title: "OIC Value", dataIndex: "oic_value", key: "oic_value", render: (val) => val !== null && val !== undefined ? <Text>{typeof val === 'number' ? val.toFixed(4) : val}</Text> : <Text type="secondary">—</Text>, sorter: (a, b) => (Number(a.oic_value) || 0) - (Number(b.oic_value) || 0) },
    { title: "Match", dataIndex: "match", key: "match", render: (val) => (<Tag color={val ? 'green' : 'red'}>{val ? 'Match' : 'No Match'}</Tag>), sorter: (a, b) => {
      const av = a.match ? 1 : 0;
      const bv = b.match ? 1 : 0;
      return bv - av;
    } }
  ];

  const mappedCountries = (countryMappings || []).filter(m => m && m.c_code != null).sort((a, b) => {
    try { return Number(a.c_code) - Number(b.c_code); } catch (e) { return 0; }
  });

  const uniqueIndicators = new Map();
  (indicatorMappings || []).forEach(mapping => {
    const code = mapping.oic_ind_code;
    if (!uniqueIndicators.has(code)) {
      uniqueIndicators.set(code, {
        oic_ind_code: code,
        oic_ind_name: mapping.oic_ind_name
      });
    }
  });
  const availableIndicators = Array.from(uniqueIndicators.values()).sort((a, b) => {
    const aName = a.oic_ind_name || '';
    const bName = b.oic_ind_name || '';
    return aName.localeCompare(bName);
  });

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      {unavailableIndicators.length > 0 && (
        <Alert
          type="error"
          showIcon
          message={`${unavailableIndicators.length} indicator(s) unavailable in IMF API`}
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {unavailableIndicators.map(m => {
                const indInfo = availableIndicators.find(i => i.oic_ind_code === m.oic_ind_code);
                const displayName = indInfo?.oic_ind_name || m.oic_ind_name;
                return (
                  <li key={m.id}>
                    <Text code>{m.imf_indicator_code}</Text>
                    {m.oic_ind_code ? ` (OIC: ${m.oic_ind_code})` : ''}
                    {displayName ? ` — ${displayName}` : ''}
                  </li>
                );
              })}
            </ul>
          }
          closable
        />
      )}
      <Card>
        <Form form={form} layout="vertical" onFinish={handleSearch}>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="ind_codes" label="OIC Indicators">
                <Select
                  mode="multiple"
                  showSearch
                  allowClear
                  placeholder="Select indicators"
                  searchValue={indicatorSearchFilter}
                  onSearch={(val) => setIndicatorSearchFilter(val)}
                  autoClearSearchValue={false}
                  dropdownRender={(menu) => {
                    const filteredOptions = availableIndicators.filter(i => {
                      const searchText = `${i.oic_ind_code} ${i.oic_ind_name}`.toLowerCase();
                      return searchText.indexOf(indicatorSearchFilter.toLowerCase()) >= 0;
                    });
                    const selectedVals = form.getFieldValue("ind_codes") || [];
                    const allFilteredSelected = filteredOptions.length > 0 && filteredOptions.every(opt => selectedVals.includes(opt.oic_ind_code));
                    const someFilteredSelected = filteredOptions.some(opt => selectedVals.includes(opt.oic_ind_code));

                    return (
                      <>
                        <div style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                          <Checkbox
                            checked={allFilteredSelected}
                            indeterminate={someFilteredSelected && !allFilteredSelected}
                            onChange={(e) => {
                              const currentSelected = form.getFieldValue("ind_codes") || [];
                              const filteredCodes = filteredOptions.map(opt => opt.oic_ind_code);
                              if (e.target.checked) {
                                form.setFieldsValue({
                                  ind_codes: [...new Set([...currentSelected, ...filteredCodes])]
                                });
                              } else {
                                form.setFieldsValue({
                                  ind_codes: currentSelected.filter(code => !filteredCodes.includes(code))
                                });
                              }
                            }}
                          >
                            Select All in Results
                          </Checkbox>
                        </div>
                        {menu}
                      </>
                    );
                  }}
                  filterOption={(input, option) => {
                    const indicator = availableIndicators.find(i => i.oic_ind_code === option.value);
                    if (!indicator) return false;
                    const searchText = `${indicator.oic_ind_code} ${indicator.oic_ind_name}`.toLowerCase();
                    return searchText.indexOf(input.toLowerCase()) >= 0;
                  }}
                >
                  {availableIndicators.map(indicator => (
                    <Option key={indicator.oic_ind_code} value={indicator.oic_ind_code}>
                      {indicator.oic_ind_code} - {indicator.oic_ind_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="c_code" label="Country Filter (Optional)">
                <Select showSearch allowClear placeholder="Filter by country" optionFilterProp="children">
                  {mappedCountries.map(mapping => {
                    const ccode = mapping.c_code;
                    const local = countriesState.value?.[ccode];
                    const label = local ? (local.c_short_name_eng || local.c_full_name_eng || local.c_name_eng) : mapping.geo_area;
                    return (<Option key={ccode} value={ccode}>{ccode} - {label}</Option>);
                  })}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Years (Optional)">
                <DatePicker
                  onlyYearPicker
                  multiple
                  range
                  sort
                  highlightToday={false}
                  placeholder="Select years"
                  value={selectedYears}
                  onChange={setSelectedYears}
                  maxDate={new Date()}
                  minDate={new Date(1970, 0, 1)}
                  plugins={[
                    <DatePanel />,
                    <PresetValuesPlugin position="left" setValue={setSelectedYears} />,
                  ]}
                  style={{ width: '100%', height: '32px', marginTop: '-1px' }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SearchOutlined />}
              loading={loading}
            >
              Convert / Fetch
            </Button>
            <Button
              icon={<ClearOutlined />}
              onClick={() => { form.resetFields(); setData([]); setPagination({ current: 1, pageSize: 100, total: 0, totalPages: 0 }); setSelectedYears([]); }}
            >
              Clear
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={() => exportToCsv(data)}
              loading={exportLoading}
            >
              Export All Data to CSV
            </Button>
          </Space>
        </Form>
      </Card>

      <Table rowKey={(r) => r.id || `${r.oic_ind_code}_${r.c_code}_${r.year}`} loading={loading} columns={columns} dataSource={data} pagination={{ current: pagination.current, pageSize: pagination.pageSize, total: pagination.total }} onChange={(p) => handleTableChange(p)} bordered />
    </Space>
  );
}
